import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PartyPopper, Coffee, Hotel, Building2, Users, ArrowRight } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1576842546422-60562b9242ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjYXRlcmluZyUyMGV2ZW50fGVufDB8fHx8MTc3ODI2NDU4M3ww&ixlib=rb-4.1.0&q=85';

const UseCases = () => {
  useEffect(() => {
    document.title = 'Where It’s Used | Magomeal';
  }, []);

  const cases = [
    {
      to: '/use-cases/events-and-catering',
      title: 'Events and Catering',
      description: 'Make every gathering memorable and zero-waste with edible cups and cutlery designed for high-volume service.',
      icon: <PartyPopper size={32} />,
      image: '/assets/images/event.png',
    },
    {
      to: '/use-cases/coffee-producers',
      title: 'Coffee Producers',
      description: 'Pair premium coffee with edible cups for a signature, sustainable tasting experience your customers will remember.',
      icon: <Coffee size={32} />,
      image: '/assets/images/coffeeroasters.jpg',
    },
    {
      to: '/use-cases/hotels-restaurants-cafes',
      title: 'Hotels, Restaurants and Cafes',
      description: 'Differentiate your venue with eco-friendly, edible serveware that elevates your guest experience and brand story.',
      icon: <Hotel size={32} />,
      image: 'https://images.unsplash.com/photo-1538333581680-29dd4752ddf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxyZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc4MjY0NTgzfDA&ixlib=rb-4.1.0&q=85',
    },
    {
      to: '/use-cases/corporations-companies',
      title: 'Corporations and Companies',
      description: 'Live your sustainability values at every coffee break, meeting and corporate event with plastic-free alternatives.',
      icon: <Building2 size={32} />,
      image: '/assets/images/corporation.jpg',
    },
    {
      to: '/use-cases/private-gatherings',
      title: 'Private Gatherings',
      description: 'Create memorable moments at family meals, birthdays, picnics and weekend gatherings \u2013 indoors or outdoors.',
      icon: <Users size={32} />,
      image: 'https://images.unsplash.com/photo-1656615720849-db697d06d820',
    },
  ];

  return (
    <div className="usecases-overview-page">
      {/* Hero */}
      <section className="usecase-hero">
        <div className="container usecase-hero-container">
          <div className="usecase-hero-text">
            <span className="usecase-eyebrow">Where It’s Used</span>
            <h1 className="display-medium">Sustainable solutions for every industry</h1>
            <p className="body-large">
              From large-scale catering to your daily office coffee, discover how Magomeal’s edible cups and
              cutlery help businesses replace single-use plastic with delicious, zero-waste alternatives.
            </p>
            <div className="usecase-hero-actions">
              <Link to="/contact" className="btn-primary">
                Talk to our team
                <ArrowRight size={18} />
              </Link>
              <Link to="/shop" className="btn-secondary">View Products</Link>
            </div>
          </div>
          <div className="usecase-hero-image-wrap">
            <img
              src={HERO_IMG}
              alt="Where Magomeal is used across industries"
              className="usecase-hero-image"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="usecases-grid-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Choose your industry</h2>
            <p className="body-large">
              Explore tailored solutions and find the right edible option for your business.
            </p>
          </div>
          <div className="usecases-grid">
            {cases.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="usecase-card hover-lift"
                aria-label={c.title}
              >
                <div className="usecase-card-image-wrap">
                  <img src={c.image} alt={c.title} className="usecase-card-image" loading="lazy" />
                  <div className="usecase-card-icon">{c.icon}</div>
                </div>
                <div className="usecase-card-body">
                  <h3 className="network-card-title">{c.title}</h3>
                  <p className="network-card-content">{c.description}</p>
                  <span className="usecase-card-link">
                    Learn more <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="usecase-cta">
        <div className="container">
          <div className="usecase-cta-card">
            <h2 className="heading-1">Don’t see your industry?</h2>
            <p className="body-large">
              We tailor edible cups and cutlery solutions for many more sectors. Tell us about your needs and we’ll
              design something that fits.
            </p>
            <div className="usecase-cta-actions">
              <Link to="/contact" className="btn-primary">
                Get in touch
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UseCases;
